<template>
  <div @click="selectedItem = null">
    <breadcrumbs :home="recurring ? 'Recurring invoice template' : 'Invoice'">
      <template>
        <v-progress-linear striped height="6" class="mx-16" v-model="itemProgress" v-if="itemProgress != 100" style="max-width:600px"/>
      </template>
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <v-card width="400">
            <sales-preferences @update="preferences.useNumber=$event.useNumber;preferences.defaultQBO=$event.defaultQBO;preferences.defaultAllowOnlinePayments=$event.defaultAllowOnlinePayments;preferences.defaultTaxInclusive=$event.defaultTaxInclusive;preferences.defaultMessage=$event.defaultMessage;preferences.allowDiscounts=$event.allowDiscounts"/>
          </v-card>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row v-if="recurring">
            <v-col><recurring-transaction v-model="recurringTransaction"/></v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="5" md="4">
              <v-row>
                <v-col>
                  <v-autocomplete v-model="invoice.customerId" outlined dense label="Customer" :rules="[selectCustomer]"
                                  :items="customersList" item-text="name" item-value="id" @change="changeCustomer" no-data-text="No customers found"
                                  :hint="customerHint(customer)" persistent-hint ref="customer">
                    <template v-slot:prepend-item>
                      <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="customerCreate=true">
                        <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new customer
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="10">
                  <v-text-field label="Email" v-model="invoice.email" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                hint="Separate emails with a comma"/>
                </v-col>
                <v-col cols="2" class="text-caption mt-3">
                  <v-menu :close-on-content-click="false" min-width="350" :nudge-bottom="20" :disabled="!edit">
                    <template v-slot:activator="{ on }">
                      <a href="#" v-on="on">Cc/Bcc</a>
                    </template>
                    <v-card class="pa-4">
                      <v-text-field label="Cc" v-model="invoice.cc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                     hint="Separate emails with a comma"/>
                      <v-text-field label="Bcc" v-model="invoice.bcc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                     hint="Separate emails with a comma"/>
                    </v-card>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Bill to address" v-model="invoice.billToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]" hide-details="auto"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Ship to address" v-model="invoice.shipToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]" hide-details="auto"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="6" md="5" offset-sm="1">
              <sales-invoices-details :invoice="invoice" :preferences="preferences" :customer="customer" :restrictedToSalesRep.sync="restrictedToSalesRep"
                                      :cc.sync="invoice.cc" :bcc.sync="invoice.bcc" :recurring="recurring"/>
              <v-row dense>
                <v-col cols="6">
                  <v-text-field label="Customer PO" v-model="invoice.po" outlined dense :rules="[fieldRules.maxLength(20)]" counter="20"/>
                </v-col>
                <v-col cols="6">
                  <v-combobox label="Ship via" v-model="invoice.shipVia" outlined dense :rules="[fieldRules.maxLength(20)]" counter="20" :items="shipVias"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col class="text-h6 text-right pr-6" v-if="$vuetify.breakpoint.mdAndUp">
              Amount {{customer && this.company.settings.homeCurrency !== customer.currency ? customer.currency : ''}} {{total | currency}}
              <div class="text-body-1" v-if="!preferences.useNumber && invoice.numbe && !recurring">Invoice #{{invoice.number}}</div>
              <div class="text-h5 green--text" v-if="paid">PAID</div>
            </v-col>
          </v-row>

          <v-row dense>
            <v-spacer/>
            <v-col cols="12" sm="5" lg="3">
              <v-switch label="Amounts are inclusive of tax" v-model="invoice.taxInclusive" dense hide-details class="mt-0" @change="toggleTaxInclusive()"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <outlined-block v-model="invoice.lines" :rules="[lines]" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="invoice.lines" item-key="key" no-data-text="There must be at least one item"
                              :headers="headers" :expanded.sync="expanded"
                              :item-class="item => item.items ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              class="no-sort" ref="lineTableBody"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n3" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n3" icon @click.stop="deleteLine(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.item="{item}">
                    <template v-if="selectedItem === item.key">
                      <multi-level-select :menuData="types" :first.sync="item.type" :second.sync="item.id" caption @change="changeLineType(item)" autofocus outlined label="Item"/>
                      <template v-if="item.type==='Product'">
                        <v-checkbox dense v-model="item.inBondProduct" class="mt-n4" :disabled="!isProductLoaded(item)"
                                    @change="changeProductBonded(item)">
                          <template v-slot:label><span class="text-caption">Take product from in-bond inventory</span></template>
                        </v-checkbox>
                      </template>
                      <v-text-field dense v-model="item.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" placeholder="Description" class="text-caption" outlined label="Description"/>
                    </template>
                    <div v-else class="pl-1">
                      <div class="text-caption" :class="{'error--text':!item.type || !item.id}">{{itemDescription(item)}}{{item.type==='Product' ? (item.inBondProduct ? ' (in-bond)' : ' (duty-paid)') : ''}}</div>
                      <div :class="{'error--text':!item.description}">{{item.description || 'Description required'}}</div>
                    </div>
                  </template>
                  <template v-slot:item.sku="{item}">
                    <template v-if="!item.items">
                      <v-text-field v-if="selectedItem === item.key" label="SKU" dense outlined v-model="item.sku" class="text-caption" :rules="[fieldRules.maxLength(20)]"/>
                      <div v-else class="pl-1">{{item.sku}}</div>
                    </template>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input label="Quantity"
                                            v-model="item.quantity" prefix=""
                                            :suffix="suffix(item)" :precision="precision(item)" class="text-caption" @change="changeQuantityPrice(item)"
                                            :no-minimum="item.type==='Item'"/>
                      <v-checkbox v-if="item.type==='Product'" dense v-model="item.cases" hide-details @change="setProductPrice(item)" class="mt-n4">
                        <template v-slot:label><span class="text-caption">cases</span></template>
                      </v-checkbox>
                    </template>
                    <div v-else class="pr-1">
                      <div :class="{'error--text':item.quantity < 0 && item.type!=='Item'}">{{item.quantity | number(precision(item))}} {{suffix(item)}}</div>
                    </div>
                  </template>
                  <template v-slot:item.so="{item}">
                    <v-select v-if="selectedItem === item.key" label="Sales order #"
                              :items="salesOrdersForLine(item)" item-text="soNumber" item-value="salesOrderId" :return-object="false" v-model="item.salesOrderId"
                              no-data-text="" outlined
                              class="text-caption" dense @change="changedSO(item)"/>
                    <div v-else class="mr-1">{{salesOrderForLine(item)}}</div>
                  </template>
                  <template v-slot:item.price="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Unit price" v-model="item.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(item)"/>
                      <div v-else class="pr-1" :class="{'error--text':item.unitPrice<0}">${{item.unitPrice | number(4)}}</div>
                    </template>
                  </template>
                  <template v-slot:item.amount="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Amount" v-model="item.amount" class="text-caption" @change="changeAmount(item)" :no-minimum="item.type==='Item'"/>
                      <div v-else class="pr-1" :class="{'error--text':item.amount<0 && item.type!=='Item'}">{{item.amount | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:item.taxes="{item}">
                    <template v-if="!item.items">
                      <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" outlined v-model="item.taxCodeId" :rules="[connectedToAccounting?fieldRules.required:true]"
                                :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                      <div v-else class="pl-1" :class="{'error--text':connectedToAccounting && !item.taxCodeId}">{{itemTax(item)}}</div>
                    </template>
                  </template>
                  <template v-slot:expanded-item="{headers,item}">
                    <td :colspan="headers.length" class="pa-0">
                      <v-card flat @click:stop="() => selectedItem = item.key">
                        <v-simple-table dense style="width:100%">
                          <tr v-for="bundleItem in item.items" :key="bundleItem.key" :class="selectedItem === item.key ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''">
                            <td :width="headers[0].width" :min-width="headers[0].width" class="pa-0 v-data-table__divider text-center"/>
                            <td :width="headers[1].width" :min-width="headers[1].width" class="px-1 py-2 v-data-table__divider">
                              <v-text-field v-if="selectedItem === item.key" dense v-model="bundleItem.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" label="Description" class="text-caption" outlined hide-details/>
                              <div v-else class="pl-1">{{bundleItem.description}}</div>
                            </td>
                            <td :width="headers[2].width" :min-width="headers[2].width" class="px-1 py-2 v-data-table__divider">
                              <v-text-field v-if="selectedItem === item.key" dense v-model="bundleItem.sku" label="SKU" class="text-caption" :rules="[fieldRules.maxLength(20)]" outlined hide-details/>
                              <div v-else class="pl-1">{{bundleItem.sku}}</div>
                            </td>
                            <td :width="headers[3].width" :min-width="headers[3].width" class="px-2 v-data-table__divider text-right">
                              {{item.quantity}} x {{bundleItem.quantity | number(precision(bundleItem))}} {{suffix(bundleItem)}}
                            </td>
                            <td :width="headers[4].width" :min-width="headers[4].width" class="px-1 v-data-table__divider">&nbsp;</td>
                            <td :width="headers[5].width" :min-width="headers[5].width" class="px-1 py-2 v-data-table__divider text-right">
                              <decimal-number-input v-if="selectedItem === item.key" outlined label="Unit price" v-model="bundleItem.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(bundleItem, item)" hide-details/>
                              <div v-else class="pl-1">{{bundleItem.unitPrice | currency}}</div>
                            </td>
                            <td :width="headers[6].width" :min-width="headers[5].width" class="px-1 py-2 v-data-table__divider text-right">
                              <decimal-number-input v-if="selectedItem === item.key" outlined label="Amount" v-model="bundleItem.amount" class="text-caption" @change="changeAmount(bundleItem)" :no-minimum="bundleItem.type==='Item'" hide-details/>
                              <div v-else class="pl-1">{{bundleItem.amount | currency}}</div>
                            </td>
                            <td :width="headers[7].width" :min-width="headers[7].width" class="px-1 v-data-table__divider">
                              <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" v-model="bundleItem.taxCodeId" :rules="[fieldRules.required]" outlined hide-details
                                        :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                              <div v-else class="pl-1">{{itemTax(bundleItem)}}</div>
                            </td>
                            </tr>
                        </v-simple-table>
                      </v-card>
                    </td>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab color="primary" x-small @click.stop="addLine" :disabled="!addOkay || !edit"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
              <div class="warning--text mb-2" v-if="nonSOInventory">{{nonSOInventory}}</div>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" order="2" order-sm="1">
              <v-container class="pa-0">
                <v-row>
                  <v-col>
                    <v-textarea label="Message to customer" v-model="invoice.memo" outlined dense rows="2" :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea label="Note" rows="2"
                                  v-model="invoice.privateNote" outlined dense :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="1">
                    <attachment v-model="invoice.attachmentURL" :read-only="!edit" @change="invoice.attachmentUpload=invoice.attachmentURL" @delete="invoice.attachmentDelete=true"/>
                  </v-col>
                  <v-spacer/>
                  <v-col cols="6" sm="4" md="3" v-if="customer && company.settings.homeCurrency !== customer.currency">
                    <decimal-number-input class="text-caption" :label="`1 ${customer.currency} =`" outlined v-model="invoice.exchangeRate" prefix="" :suffix="` ${company.settings.homeCurrency}`" :precision="6" hide-details/>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" sm="6" order="1" order-sm="2">
              <v-container class="pa-0">
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Subtotal</v-col>
                  <v-col cols="4" class="text-right pr-6">{{subTotal | currency}}</v-col>
                </v-row>
                <v-row dense align="center" v-if="preferences.allowDiscounts || invoice.discount.amount">
                  <v-col cols="5" md="4" offset-md="2">
                    <v-select v-model="invoice.discount.percent" :items="[{text:'Discount percent',value:true},{text:'Discount value',value:false}]"
                              dense outlined hide-details class="text-caption"
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="3" md="2">
                    <decimal-number-input v-model="invoice.discount.amount"
                              :prefix="invoice.discount.percent?'':'$'" :suffix="invoice.discount.percent?'%':''"
                              :max="invoice.discount.percent?100:undefined"
                              hide-details caption
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="4" class="text-right pr-6">{{discountValue?'-':''}}{{discountValue | currency}}</v-col>
                </v-row>
                <template v-for="tax of invoice.taxLines">
                  <v-row dense v-if="tax.name!=='Exempt' && tax.name!=='Out of Scope'" :key="tax.id" align="center">
                    <v-col cols="8" class="font-weight-medium text-right">{{invoice.taxInclusive?'Includes ':''}}{{tax.name}} @{{tax.percent}}% on {{tax.onAmount | currency}}</v-col>
                    <v-col cols="3" offset="1" class="text-right pr-6">
                      <decimal-number-input v-model="tax.amount" hide-details no-minimum/>
                    </v-col>
                  </v-row>
                </template>
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Total {{customer && company.settings.homeCurrency !== customer.currency ? `(${customer.currency})` : ''}}</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total | currency}}</v-col>
                </v-row>
                <v-row dense v-if="customer && company.settings.homeCurrency !== customer.currency">
                  <v-col cols="8" class="font-weight-medium text-right">Total ({{company.settings.homeCurrency}})</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total*invoice.exchangeRate | currency}}</v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>

        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="!invoiceId || !invoice.recurringTransaction">
          <v-switch v-if="invoiceId && company.permissions.includes('selling.write') && !invoice.draft && !invoice.voided && !originalDraft"
                    v-model="edit" label="Edit" hide-details class="mt-0 mr-4"
                    :disabled="dirty || loading || paid"/>
          <v-switch dense hide-details label="Make recurring" class="mt-0" v-model="recurring"
                    v-if="edit && company.allowRecurringTransactions && (!invoiceId || !invoice.recurringTransaction)" :disabled="onlyRecurring"/>
        </template>
        <div v-if="invoice.voided" class="warning--text">Voided invoices cannot be edited</div>
        <v-spacer/>
        <v-checkbox v-if="edit" dense hide-details label="Draft" v-model="invoice.draft" class="mr-2 mt-n1"/>
        <v-btn :plain="edit" color="primary" @click="cancel" :disabled="loading">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save()" :disabled="itemProgress!==100">Save{{recurring?' template':''}}</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <customer-quick-create v-model="customerCreate" @create="newCustomer" :sales-rep-id="restrictedToSalesRep"/>

    <v-dialog v-model="showConfirm" persistent max-width="500">
      <v-card>
        <v-card-title>Save</v-card-title>
        <v-divider/>
        <v-card-text class="pa-4 text-body-1">
          <div>There are items on the invoice that are not being sold against a Shipment or Sales order.</div>
          <div class="mt-3 warning--text">If you proceed, be aware that inventory will <em>not</em> automatically be updated for these items.</div>
          <div class="mt-3">(You may want to cancel and create a Shipment or Sales order, first.)</div>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-spacer/>
          <v-btn plain color="warning" @click="showConfirm=false;save2()">Save</v-btn>
          <v-btn color="primary" @click="showConfirm=false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

<!--
    <v-dialog v-model="showMatchInvoice" persistent max-width="500">
      <v-card>
        <v-card-title>Match invoice</v-card-title>
        <v-divider/>
        <v-card-text class="pa-4 text-body-1">
          <div>
            The following existing invoices might match the shipment you are creating an invoice for.
            You may choose to match one of these invoices instead of creating a new one.
          </div>
          <v-radio-group v-model="matchedInvoiceId">
            <template v-for="invoice in invoices">
              <v-radio :value="invoice.id" :label="invoice.description"/>
            </template>
          </v-radio-group>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-spacer/>
          <v-btn plain @click="showMatchInvoice=false">Cancel</v-btn>
          <v-btn color="primary" @click="matchInvoice()">Match</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
-->
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import sortableJS from 'sortablejs';
import CustomerQuickCreate from '@/views/sales/customers/CustomerQuickCreate'
import confirmLeave from '@/mixins/confirmLeave'
import customerTypes from '@/mixins/customerTypes'
import createHierarchyList from '@/mixins/createHierarchyList'
import SalesPreferences from '@/views/sales/SalesPreferences'
import SalesInvoicesDetails from '@/views/sales/invoices/SalesInvoicesDetails'
import {add,format} from 'date-fns';
import RecurringTransaction from '@/components/RecurringTransaction'
import Attachment from '@/components/Attachment'

export default {
  name: 'SalesInvoicesEdit',
  mixins: [confirmLeave,createHierarchyList,customerTypes],
  components: {CustomerQuickCreate,SalesInvoicesDetails,SalesPreferences,RecurringTransaction,Attachment},
  props: {
    invoiceId: {type:String, required:false},
    salesOrderId: {type:String, required:false},
    shipmentId: {type:String, required:false},
    customerId: {type:String, required:false},
    copyInvoiceId: {type:String, required:false},
    onlyRecurring: {type:Boolean, required:false},
    fromRecurringTransactionId: {type:String, required:false},
    date: {type:String, required:false}
  },
  data () {
    return {
      edit: false,
      recurring: this.onlyRecurring,
      recurringTransaction: {},
      preferences: {},
      province: null,
      taxes: [],
      shipVias: [],
      customers: [],
      customerCreate:false,
      paid: false,

      spirits: [],
      wines: [],
      nonExcises: [],
      items: [],
      bundles: [],
      products: [],
      customerTypes: [],
      itemCategories: [],
      productCategories: [],
      invoice: {customerId:'',billToAddress:'',shipToAddress:'',taxInclusive:false,date:'',due:'',draft:false, email:'', cc:'', bcc:'',
                  accountRef:'',paymentMethodId:'',qbo:false, allowOnlinePayments:true, paymentRefNum:'',
                  po:'',shipVia:'',lines:[],taxLines:[],memo:'',privateNote:'',termId:'',salesRepId:'',exchangeRate:1,
                  discount:{percent:true,amount:0}},
      dirty: false,
      saving: false,
      loading: false,
      originalDraft: false,
      itemProgress: 100,
      valid: true,
      expanded: [],
      moveOffset: 0,
      subTotal: 0,
      showConfirm: false,
      selectedItem: null,
      restrictedToSalesRep: '',
      lines: v => v.length>0 || 'There must be at least one line item',
      selectCustomer: v => !!v || 'Select a customer',
      itemValid: id => id !== '' || 'Please select',
      inputLine: null,
      matchedInvoiceId: '',
      invoices: [],
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    customer() {
      return this.customers.find(customer => customer.id === this.invoice.customerId);
    },
    customersList() {
      return this.restrictedToSalesRep
              ? this.customers.filter(customer => customer.salesRep?.id === this.restrictedToSalesRep ||
                                                  customer.id === this.invoice.customerId)
              : this.customers;
    },
    headers() {
      const headers = [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                      { text: 'Item/Description', value: 'item', width:'25%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                      { text: 'SKU', value: 'sku' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                      { text: 'Quantity', value: 'quantity' ,width:'12.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'}];
      if (!this.recurring)
        headers.push({ text: 'SO #', value: 'so' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true});
      headers.push(   { text: 'Unit price', value: 'price' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Amount', value: 'amount' ,width:'12.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Tax', value: 'taxes' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true});
      return headers;
    },
    types() {
      const types = [];
      types.push({name:'Product',id:'Product',menu:this.menuForProducts(this.products)});
      if (this.bundles.length)
        types.push({name:'Bundle',id:'Bundle',menu:this.bundles});
      if (this.items.length) {
        if (this.itemCategories.length) {
          const categories = this.createHierarchyList(this.itemCategories);
          for (const category of categories) {
            const sub = this.items.filter(item => item.category?.id === category.id);
            if (sub.length)
              category.menu = sub;
            else
              category.disabled = true;
          }
//          types.push({name:'Item',id:'Item',menu:[...categories.filter(category => !category.disabled)]});
          types.push({name:'Item',id:'Item',menu:categories});
          const leftOver = this.items.filter(item => !item.category?.id);
          if (leftOver.length)
            types[types.length-1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
        }
        else
          types.push({name:'Item',id:'Item',menu:this.items});
      }
      if (this.spirits.length)
        types.push({name:'Spirit',id:'Spirit',menu:this.spirits});
      if (this.wines.length)
        types.push({name:'Wine',id:'Wine',menu:this.wines});
      if (this.nonExcises.length)
        types.push({name:'Non-Excise',id:'NonExcise',menu:this.nonExcises});
      return types;
    },
    addOkay() {
      return this.types.length;
    },
    values() {
      return this.company.permissions.includes('values');
    },
    nonSOInventory() {
      if (!this.invoice.shipmentId && this.invoice.lines?.some(line => !line.salesOrderId && (line.type !== 'Item' || this.items.find(item => item.id === line.id)?.type === 'Item')))
        return 'There are items not invoiced against a shipment or sales order. Inventory will not be updated!';
    },
    actualSubtotal() {
      return this.invoice.taxInclusive
              ? this.$roundNumber(this.invoice.lines.reduce((total,line) =>
                              total + line.amount/ (1+(this.taxes.find(tax => tax.id === line.taxCodeId)?.salesRate??0)/100), 0))
              : this.subTotal;
    },
    discountValue() {
      if (this.invoice.discount.percent)
        return this.$roundNumber(this.actualSubtotal * this.invoice.discount.amount/100);
      return this.invoice.discount.amount;
    },
    total() {
      return this.actualSubtotal - this.discountValue + this.invoice.taxLines.reduce((total,tax) => tax.amount + total, 0);
    }
  },
  watch: {
    invoice: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    }
  },
  async created() {
    this.loading = true;
    if (this.invoiceId || this.salesOrderId || this.shipmentId || this.copyInvoiceId)
      this.itemProgress = 0;
    try {
      this.province = await import(`@/views/provinces/${this.company.province}/sales/shipments.js`);
    // eslint-disable-next-line no-empty
    } catch (error) {}

    const data = await Promise.all([
      this.$store.dispatch('customers', !this.invoiceId),
      this.$store.dispatch('products'),
      this.$store.dispatch('items'),
      this.$lazyListLoad(this.productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(this.itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
//      this.$lazyListLoad(this.products, 'products2', 'id,name,category{id}', 100, !this.invoiceId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.bundles, 'bundles2', 'id,name,price,inBondPrice,tax{id},sellingDescription,sku,displayComponents', 100, !this.invoiceId ? [{field:'active', comparator:'eq', value:'true'}] : []),
//      this.$lazyListLoad(this.items, 'items2', 'id,type,name,trackUnits,precision,category{id}', 100, [{field:'sold', comparator:'eq', value:'true'}, ...(!this.invoiceId ? [{field:'active', comparator:'eq', value:'true'}] : [])]),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.invoiceId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.company.allowWine ?  this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.invoiceId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : [],
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.invoiceId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,salesRate,groupTaxes', 100, this.invoiceId ? [] : [{field:'active', comparator:'eq', value:'true'},{field:'salesRate', comparator:'ne', value:''}])
    ]);
    this.customers = data[0];
    this.products = this.invoiceId ? data[1] : data[1].filter(product => product.active);
    this.items = this.invoiceId ? data[2].filter(item => item.sold) : data[2].filter(item => item.sold && item.active);
    const results = await this.$apiCall(`query SalesInvoicesEdit($edit:Boolean!, $id:ID!, $salesOrder:Boolean!, $salesOrderId:ID!, $shipment:Boolean!, $shipmentId:ID!) {
                                              invoice(id:$id) @include(if:$edit) {
                                                id,draft,voided,customerId,shipToAddress,billToAddress,email,cc,bcc,date,due,memo,privateNote,shipmentId
                                                number,po,termId,salesRepId,shipVia,exchangeRate,taxInclusive,qbo,allowOnlinePayments,paymentMethodId,accountRef,paymentRefNum
                                                attachmentURL
                                                paid
                                                lines {
                                                  type,id,cases,inBondProduct,description,sku,quantity,salesOrderId,unitPrice,amount,taxCodeId
                                                  items {type,id,cases,inBondProduct,description,sku,quantity,salesOrderId,unitPrice,amount,taxCodeId}
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                recurringTransaction {
                                                  name,scheduleType,advanceDays,startDate,scheduleInterval
                                                  frequency,dayOfWeek,ordinal,day,month
                                                  end,endDate,endOccurances
                                                }
                                                discount {percent, amount}
                                              }
                                              invoiceNextNumber
                                              salesOrder(id:$salesOrderId) @include(if:$salesOrder) {
                                                customerId,shipToAddress,billToAddress,email,bcc,cc,termId,salesRepId,po,shipVia,exchangeRate,taxInclusive
                                                lines {
                                                  type,id,cases,inBondProduct,description,sku,quantity,shippedQuantity,invoicedQuantity,unitPrice,amount,taxCodeId
                                                  items {type,id,cases,inBondProduct,description,sku,quantity,shippedQuantity,invoicedQuantity,unitPrice,amount,taxCodeId}
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                discount {percent, amount}
                                              }
                                              shipment(id:$shipmentId) @include(if:$shipment) {
                                                customer{id},po,shipVia,date
                                                shipToAddress,billToAddress,email,bcc,cc,invoice{termId,salesRepId,discount {percent, amount}}
                                                lines {
                                                  type,id,cases,inBondProduct,description,sku,quantity,unitPrice,amount,taxCodeId,salesOrderId
                                                  items {type,id,cases,inBondProduct,description,sku,quantity,unitPrice,amount,taxCodeId,salesOrderId}
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                              }
                                              salesPreferences {
                                                defaultMessage,alwaysInBond,allowDiscounts
                                                useNumber,defaultQBO,defaultTaxInclusive,defaultAllowOnlinePayments
                                                defaultCc,defaultBcc,defaultCcSalesRep,defaultBccSalesRep
                                              }
                                              shipVias {
                                                name
                                              }
                                          }`,
                                        {edit:this.invoiceId!=undefined || this.copyInvoiceId!=undefined,
                                         id:this.invoiceId ?? this.copyInvoiceId ?? '',
                                         salesOrder:!!this.salesOrderId,salesOrderId:this.salesOrderId ?? '',
                                         shipment:!!this.shipmentId,shipmentId:this.shipmentId ?? ''}
                                         );
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.preferences = results.data.salesPreferences;
    this.shipVias = results.data.shipVias.map(shipVia => shipVia.name);
    this.customerTypes = await this.buildCustomerTypes();

    if (this.invoiceId || this.copyInvoiceId) {
      let key = Date.now();
      results.data.invoice.lines.forEach(item => item.key = key++);
      this.invoice = results.data.invoice;
      let n = 0;
      for (const line of this.invoice.lines) {
        if (this.copyInvoiceId) {
          line.salesOrderId = '';
          line.lot = '';
        }
        await this.setLineDetails(line, true);

        if (this.invoice.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.invoice.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
        this.itemProgress = (n++ / this.invoice.lines.length) * 100;
      }
      this.recurring = !!this.invoice.recurringTransaction && !this.copyInvoiceId;
      this.recurringTransaction = this.invoice.recurringTransaction ?? {};
      this.originalDraft = this.invoice.draft;
      if (this.copyInvoiceId) {
        this.invoice.date = this.date ?? format(new Date(), 'yyyy-MM-dd');
        this.invoice.number = results.data.invoiceNextNumber;
      }
      this.paid = this.invoice.paid;
      delete this.invoice.paid;
      this.edit = !this.paid || this.invoice.pendingReview || this.originalDraft || !!this.copyInvoiceId || this.recurring;
    }
    else {
      this.edit = true;
      this.invoice.taxInclusive = this.preferences.defaultTaxInclusive;
      if (this.salesOrderId || this.shipmentId) {
        const copyFrom = results.data[this.salesOrderId ? 'salesOrder' : 'shipment'];
        this.invoice.customerId = copyFrom.customerId ?? copyFrom.customer.id;
        this.invoice.billToAddress = copyFrom.billToAddress;
        this.invoice.shipToAddress = copyFrom.shipToAddress;
        this.invoice.email = copyFrom.email;
        this.invoice.cc = copyFrom.cc;
        this.invoice.bcc = copyFrom.bcc;
        this.invoice.po = copyFrom.po;
        this.invoice.shipVia = copyFrom.shipVia;
        this.invoice.discount = this.salesOrderId ? copyFrom.discount : (copyFrom.invoice?.discount ?? {percent:true,amount:0});
        this.invoice.exchangeRate = copyFrom.exchangeRate ?? this.invoice.exchangeRate;
        this.invoice.taxInclusive = copyFrom.taxInclusive ?? this.invoice.taxInclusive;
        this.invoice.taxLines = copyFrom.taxLines;
        let n = 0;
        for (const copyFromLine of copyFrom.lines) {
          const quantity = this.salesOrderId ? copyFromLine.quantity - copyFromLine.invoicedQuantity : copyFromLine.quantity;
          if (quantity) {

            if (copyFromLine.type === 'Tank' || copyFromLine.type === 'BulkContainer') {     // Creating invoice from a shipment
              const resultsVessel  = await this.$apiCall(`query SalesInvoicesEdit($vesselId:ID!, $date:DateTime) {
                                                            exciseVessel(id:$vesselId) {
                                                              contents(date:$date) {alcoholType,alcoholId}
                                                            }
                                                          }`, {vesselId: copyFromLine.id, date: format(add(new Date(copyFrom.date), {seconds:-1}), 'yyyy-MM-dd HH:mm:ss')});
              copyFromLine.id = resultsVessel.data.exciseVessel.contents.alcoholId;
              copyFromLine.type =  resultsVessel.data.exciseVessel.contents.alcoholType;
              if (copyFromLine.type !== 'Spirit' && copyFromLine.type !== 'NonExcise')
                copyFromLine.type = 'Wine';
            }

            const amount = copyFromLine.amount ? this.$roundNumber(quantity / copyFromLine.quantity * copyFromLine.amount) : 0;
            const line = {type:copyFromLine.type,id:copyFromLine.id,cases:copyFromLine.cases,inBondProduct:copyFromLine.inBondProduct,description:copyFromLine.description,sku:copyFromLine.sku,quantity,
                          salesOrderId:this.salesOrderId??copyFromLine.salesOrderId,
                          amount,taxCodeId:copyFromLine.taxCodeId,lot:null,unitPrice:amount/quantity,items:copyFromLine.items};
            this.invoice.lines.push(line);
            await this.setLineDetails(line, true);
            if (this.invoice.taxInclusive && line.taxCodeId) {
              const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
              line.unitPrice += line.unitPrice * tax.salesRate/100;
              line.amount = this.$roundNumber(line.unitPrice * line.quantity);
            }
          }
          this.itemProgress = (n++ / copyFrom.lines.length) * 100;
        }
        this.invoice.shipmentId = this.shipmentId;
        this.invoice.termId = this.salesOrderId ? results.data.salesOrder.termId : (results.data.shipment.invoice?.termId);
        this.invoice.salesRepId = this.salesOrderId ? results.data.salesOrder.salesRepId : (results.data.shipment.invoice?.salesRepId);
      }
      else {
        this.invoice.cc = this.preferences.defaultCc;
        this.invoice.bcc = this.preferences.defaultBcc;
        this.invoice.customerId = this.customerId;
        this.changeCustomer();
      }
      this.invoice.memo = this.preferences.defaultMessage;
    }
    this.calculateTotals(!this.invoice.taxLines);
    this.$nextTick(() => {this.dirty = false});

    // Check to see if there is a potential match to an existing invoice
/*
    if (this.shipmentId) {
      const invoices =  await this.$apiCall(`query SalesInvoicesEdit($customerId:ID!, $total:Float!) {
                                                invoices(customerId:$customerId, total:$total) {
                                                  id,number,date
                                                }
                                              }`,
                                              {customerId:this.invoice.customerId, total:this.total});
      if (invoices.data.invoices.length) {
        this.invoices = invoices.data.invoices.map(invoice => ({id:invoice.id, description:`Invoice #${invoice.number}, ${this.$formatDate(invoice.date)}`}));
        this.showMatchInvoice = true;
      }
    }
*/

    this.itemProgress = 100;
    this.loading = false;
  },

  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.lineTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        oldIndex -= _self.moveOffset;
        const lineSelected = _self.invoice.lines.splice(oldIndex, 1)[0];
        _self.invoice.lines.splice(newIndex, 0, lineSelected);
        _self.buildExpandedList();
      },
      onStart({oldIndex}) {
        let offset = 0;
        for (let index = 0; index < oldIndex-offset; index++) {
          const line = _self.invoice.lines[index];
          if (_self.expanded.some(e => e.key === line.key))
            offset += 1;
        }
        _self.moveOffset = offset;
        _self.expanded.splice(0, _self.expanded.length);
      }
    })

    if (!this.invoiceId)
      this.$refs.form.validate();
    setTimeout(() => this.$refs.customer.focus(), 1000);
  },
  beforeDestroy() {
    this.sortable.destroy();
  },

  methods: {
    itemDescription(item) {
      const type = this.types.find(type => type.id === item.type);
      if (!type)
        return 'MISSING';

      const lists = {'Product':[this.products], 'Item': [this.items], 'Bundle': [this.bundles], 'Spirit':[this.spirits], 'Wine':[this.wines], 'NonExcise':[this.nonExcises]};
      let name;
      for (const list of lists[type.id]) {
        name = list.find(i => i.id === item.id)
        if (name)
          break;
      }

      return `${type.name}: ${name?.name}`;
    },
    itemTax(item) {
      if (this.connectedToAccounting && !item.taxCodeId)
        return 'Required';
      return this.taxes.find(tax => tax.id === item.taxCodeId)?.name;
    },
    async matchInvoice() {
      if (this.matchedInvoiceId) {
        const results = await this.$apiCall(`mutation SalesInvoicesEdit($shipmentId:ID!,$invoiceId:ID!) {
                                              shipmentMatchInvoice(shipmentId:$shipmentId, invoiceId:$invoiceId) {id}
                                            }`,
                                            {shipmentId:this.shipmentId, invoiceId:this.matchedInvoiceId});
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`Matched shipment to existing invoice`});
        }
      }
    },
    menuForProducts(products) {
      if (this.productCategories.length) {
        const categories = this.createHierarchyList([...this.productCategories]);
        for (const category of categories) {
          const sub = products.filter(product => product.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
//        const p = [...categories.filter(category => !category.disabled)];
        const p = categories;
        const leftOver = products.filter(product => !product.category?.id);
        if (leftOver.length)
          p.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
        return p;
      }
      else
        return products;
    },
    calculateTotals(calculateTaxes) {
      this.subTotal = this.invoice.lines.reduce((total,line) =>
        total + (line.items ? line.items.reduce((total,line) => total+line.amount, 0) : line.amount)
      , 0);

      if (calculateTaxes) {
        this.invoice.taxLines = [];
        this.invoice.lines.forEach(line => {
          if (line.items)    // Process bundle items
            line.items.forEach(item => this.calculateTaxesOnLine(item));
          else
            this.calculateTaxesOnLine(line);
        });
      }
    },
    calculateTaxesOnLine(line) {
      const discount = 1 - (this.subTotal ? (this.invoice.discount.percent ? this.invoice.discount.amount/100 : this.invoice.discount.amount / this.subTotal) : 0);
      if (line.taxCodeId) {
        const taxDefinition = this.taxes.find(t => t.id === line.taxCodeId);
        if (!taxDefinition)
            return;
        for (const taxCodeId of taxDefinition.groupTaxes ?? [line.taxCodeId]) {
          let tax = this.invoice.taxLines.find(tax => tax.id === taxCodeId);
          if (!tax) {
            const taxDefinitionToUse = this.taxes.find(t => t.id === taxCodeId);
            if (!taxDefinitionToUse)
                return;
            tax = {id:taxCodeId,name:taxDefinitionToUse.name,percent:taxDefinitionToUse.salesRate,amount:0,onAmount:0};
            this.invoice.taxLines.push(tax);
          }
          tax.onAmount += discount * (this.invoice.taxInclusive ? line.amount/(1+tax.percent/100) : line.amount);
          tax.amount += this.$roundNumber(discount * (this.invoice.taxInclusive ? line.amount - line.amount/(1+tax.percent/100) : line.amount * tax.percent / 100));
        }
      }
    },
    suffix(line) {
      if (line.type === 'Product') {
        if (line.cases)
          return 'case(s)';
        const product = this.products.find(p => p.id === line.id);
        if (product?.bottlesPerUnit > 1)
          return 'unit(s)';
        if (product?.mixedPack)
          return 'pack(s)';
        if (product?.containerType === 'Can')
          return 'can(s)';
        return 'bottle(s)';
      }
      if (line.type === 'Spirit')
        return 'LAA';
      if (line.type === 'Wine' || line.type === 'NonExcise') {
          return 'litres'
      }
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.trackUnits;
      if (line.type === 'Bundle')
        return '';
      return null;
    },
    precision(line) {
      if (line.type === 'Product')
        return 0;
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.precision;
      if (line.type === 'Bundle')
        return 0;
      return 3;
    },
    customerHint(customer) {
      const noType = customer?.type === 'None';
      return `${noType ? '' : `Type: ${this.customerTypes.find(type => type.value === customer?.type)?.text}`}${customer?.category?.id ? `${noType?'': ', '}Category: ${customer.category.name}` : ''}`;
    },
    async addLine() {
      const line = {key:Date.now(),type:'',id:'',inBondProduct:this.preferences.alwaysInBond,cases:false,description:'',errorMessage:null,amount:0,unitPrice:0,quantity:1,taxCodeId:null};
      this.invoice.lines.push(line);
      this.setLineDetails(line, false);
      this.selectedItem = line.key;
      this.$nextTick(() => this.$refs.form.validate());
    },
    deleteLine(index) {
      this.invoice.lines.splice(index, 1);
      this.buildExpandedList();
      this.calculateTotals(true);
    },
    buildExpandedList() {
      this.expanded.splice(0, this.expanded.length);
      for (const line of this.invoice.lines) {
        if (line.type === 'Bundle') {
          const bundle = this.bundles.find(bundle => bundle.id === line.id);
          if (bundle.displayComponents)
            this.expanded.push(line);
        }
      }
    },

    async loadProduct(line) {
      const product = this.products.find(product => product.id === line.id);
      if (product.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesInvoicesEdit($id:ID!,$edit:Boolean!) {
                                            product(id:$id) {
                                              sku,bottleSize,caseSize,bottlesPerUnit
                                              mixedPack,containerType
                                              defaultBondedPrice,defaultBondedTax{id},sellingBondedDescription,
                                              defaultPrice,defaultTax{id},sellingDescription,
                                              bondedCustomers{customerId,tax{id},price,sku}
                                              customers{customerId,tax{id},price,sku}
                                               outstandingSalesOrdersForInvoicing(edit:$edit) {
                                                 salesOrderId,customerId,soNumber,soDate,quantityOutstanding,unitCost,taxCodeId,inBondProduct
                                               }
                                            }
                                          }`,
                                          {id:product.id,edit:!!this.invoiceId});
      this.loading = false;
      if (!results.error) {
        Object.assign(product, results.data.product);
        product.loaded = true;
      }
    },

    async loadItem(line) {
      const item = this.items.find(item => item.id === line.id);
      if (item.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesInvoicesEdit($id:ID!,$edit:Boolean!) {
                                            item(id:$id) {
                                              sku,defaultPrice,defaultTax{id},sellingDescription,
                                              customers{customerId,tax{id},price,sku}
                                               outstandingSalesOrdersForInvoicing(edit:$edit) {
                                                 salesOrderId,customerId,soNumber,soDate,quantityOutstanding,unitCost,taxCodeId
                                               }
                                            }
                                          }`,
                                          {id:item.id,edit:!!this.invoiceId});
      this.loading = false;
      if (!results.error) {
        Object.assign(item, results.data.item);
        item.loaded = true;
      }
    },

    async loadAlcoholItem(line) {
      const item = this.getSourceItem(line);
      if (item.loaded)
        return;

      if (item.type === null)
        return;
      let type = line.type.toLowerCase();
      type = {nonexcise:'nonExcise',winehigh:'wine',winemedium:'wine',wineweak:'wine'}[type] ?? type;

      this.loading = true;
      const results = await this.$apiCall(`query SalesInvoicesEdit($id:ID!,$edit:Boolean!) {
                                             ${type}(id:$id) {
                                               sku,defaultPrice,defaultTax{id},sellingDescription,customers{customerId,tax{id},price,sku}
                                               outstandingSalesOrdersForInvoicing(edit:$edit) {
                                                 salesOrderId,customerId,soNumber,soDate,quantityOutstanding,unitCost,taxCodeId
                                               }
                                              }
                                            }`,
                                          {id:item.id,edit:!!this.invoiceId});
      this.loading = false;
      if (!results.error) {
        Object.assign(item, results.data[type]);
        item.loaded = true;
      }
    },

    async loadBundle(line) {
      const bundle = this.bundles.find(bundle => bundle.id === line.id);
      if (bundle.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesInvoicesEdit($id:ID!,$edit:Boolean!) {
                                            bundle(id:$id) {
                                              items {name,type,id,quantity,price}
                                               outstandingSalesOrdersForInvoicing(edit:$edit) {
                                                 salesOrderId,customerId,soNumber,soDate,quantityOutstanding,unitCost,taxCodeId
                                               }
                                            }
                                          }`,
                                          {id:bundle.id,edit:!!this.invoiceId});
      this.loading = false;
      if (!results.error) {
        Object.assign(bundle, results.data.bundle);
        bundle.loaded = true;
      }
    },

    changeLineType(line) {
      line.unitPrice = 0;
      line.taxCodeId = null;
      line.description = '';
      line.sku = '';
      this.setLineDetails(line, false);
    },

    getSourceItem(line) {
      const list = {'Item':this.items,'Spirit':this.spirits,'Wine':this.wines,'WineHigh':this.wines,'WineMedium':this.wines,'WineWeak':this.wines,'NonExcise':this.nonExcises,'Product':this.products,'Bundle':this.bundles};
      return list[line.type]?.find(item => item.id === line.id) ?? {};
    },

    async setLineDetails(line, init) {
      if (line.type !== 'Bundle')
        delete line.items;

      if (line.id === '') {
        this.$nextTick(() => this.$refs.form?.validate());
        return;
      }

      if (line.type === 'Product')
        await this.loadProduct(line);
      else if (line.type === 'Item')
        await this.loadItem(line);
      else if (line.type === 'Spirit' || line.type === 'Wine' || line.type === 'NonExcise')
        await this.loadAlcoholItem(line);
      else if (line.type === 'Bundle')
        await this.loadBundle(line);

      this.buildExpandedList();

      if (!init) {
        if (!this.getSourceItem(line)?.outstandingSalesOrdersForInvoicing?.some(
                                      so => so.customerId === this.customer?.id &&
                                            (line.type!=='Product' || line.inBondProduct===so.inBondProduct) &&
                                            so.salesOrderId === line.salesOrderId))
          line.salesOrderId = '';
        let customerPricing;
        if (line.type === 'Bundle') {
          const bundle = this.bundles.find(bundle => bundle.id === line.id);
          if (bundle.displayComponents) {
            this.$set(line, "items", []);
            let key = Date.now();
            for (const item of bundle.items) {
              const bundleLine = {key:key++,type:item.type,name:item.name,id:item.id,description:'',errorMessage:null,
                                  amount:0,unitPrice:item.price,quantity:item.quantity,taxCodeId:null};
              line.items.push(bundleLine);
              this.setLineDetails(bundleLine, false);
            }
          }
          else {
            delete line.items;
            line.unitPrice = !this.company.settings.lotTrackingForProducts && this.customerTypes.find(type => type.value === this.customer?.type)?.prefInBond ? bundle.inBondPrice : bundle.price;
            line.taxCodeId = bundle.tax?.id;
            this.calculateUnitPrice(line);
            line.sku = bundle.sku;
            this.changeQuantityPrice(line);
          }
          line.description = bundle.sellingDescription || bundle.name;
        }
        else if (line.type === 'Product') {
          line.inBondProduct = !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond || this.preferences.alwaysInBond;
          line.cases = this.customerTypes.find(type => type.value === this.customer?.type)?.prefCases ?? false;
          this.changeProductBonded(line);
        }

        else {
          const item = {Item:this.items, Spirit:this.spirits, Wine:this.wines, NonExcise:this.nonExcises}[line.type].find(item => item.id === line.id);
          customerPricing = item.customers.find(customer => customer.customerId === this.invoice.customerId) ??
                            item.customers.find(customer => customer.customerId === this.customer?.category?.id);
          line.unitPrice = customerPricing?.price || item.defaultPrice;
          line.taxCodeId = customerPricing?.tax.id || item.defaultTax?.id;
          this.calculateUnitPrice(line);
          line.description = line.description || item.sellingDescription || item.name;
          line.sku = customerPricing?.sku || item?.sku;
          this.changeQuantityPrice(line);
        }
      }
      this.$nextTick(() => this.$refs.form?.validate());
    },
    salesOrdersForLine(line) {
      return [{salesOrderId:'',soNumber:''},...this.getSourceItem(line)?.outstandingSalesOrdersForInvoicing?.filter(so => so.customerId === this.customer?.id &&
                                                                                                            (line.type!=='Product' || line.inBondProduct===so.inBondProduct)) ?? []];
    },
    salesOrderForLine(line) {
      return this.salesOrdersForLine(line).find(so => so.salesOrderId === line.salesOrderId)?.soNumber ?? '';
    },
    changedSO(line) {
      const so = this.salesOrdersForLine(line).find(so => so.salesOrderId === line.salesOrderId);
      line.quantity = so.quantityOutstanding ?? line.quantity;
      line.amount = so.unitCost ? line.quantity * so.unitCost : line.amount;
      line.unitPrice = so.unitCost ? so.unitCost : (line.quantity ? line.amount / line.quantity : 0);
      line.taxCodeId = so.taxCodeId ?? line.taxCodeId;
      this.changeQuantityPrice(line);
    },
    isProductLoaded(line) {
      return this.products.find(product => product.id === line.id)?.loaded;
    },
    changeProductBonded(line) {
      const product = this.products.find(product => product.id === line.id);
      const inBondPricing = line.inBondProduct && !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond;
      line.description = product[inBondPricing?'sellingBondedDescription':'sellingDescription'] || product.name;
      const customerPricing = product[inBondPricing ? 'bondedCustomers' : 'customers'].find(customer => customer.customerId === this.invoice.customerId);
      line.sku = customerPricing?.sku || product?.sku;
      this.setProductPrice(line);
      this.$nextTick(() => this.$refs.form.validate());
    },
    async setProductPrice(line) {
      const product = this.products.find(product => product.id === line.id);
      const inBondPricing = line.inBondProduct && !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond;
      const productCustomers = product[inBondPricing ? 'bondedCustomers' : 'customers'];
      const customerPricing = productCustomers.find(customer => customer.customerId === this.invoice.customerId) ??
                              productCustomers.find(customer => customer.customerId === this.customer?.category?.id);
      line.unitPrice = customerPricing?.price || product[inBondPricing?'defaultBondedPrice':'defaultPrice'];
      line.taxCodeId = customerPricing?.tax.id || product[inBondPricing?'defaultBondedTax':'defaultTax']?.id;
      if (!customerPricing && this.province && !inBondPricing)
        await this.province.pricing(line, product, this.customer, this.taxes);
      if (inBondPricing) {
        if (!line.cases)
          line.unitPrice = line.unitPrice / product.caseSize;
      }
      else if (line.cases)
        line.unitPrice = line.unitPrice / product.bottlesPerUnit * product.caseSize;
      this.calculateUnitPrice(line);
      this.changeQuantityPrice(line);
    },
    calculateUnitPrice(line) {
      if (this.invoice.taxInclusive && line.taxCodeId) {
        const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
        line.unitPrice += line.unitPrice * tax.salesRate/100;
        line.amount = this.$roundNumber(line.unitPrice * line.quantity);
      }
    },
    toggleTaxInclusive() {
      for (const line of this.invoice.lines) {
        if (line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.invoice.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          else
            line.unitPrice = line.unitPrice / (1 + tax.salesRate/100)
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
      this.calculateTotals(true);
    },
    async newCustomer(customer) {
      this.customers = await this.$store.dispatch('customers', !this.invoiceId);
      this.invoice.customerId = customer.id;
    },
    async changeCustomer() {
      if (!this.customer) {
        this.invoice.email = '';
        this.invoice.billToAddress = '';
        this.invoice.shipToAddress = '';
        return;
      }

      if (!this.customer.loaded) {
        this.loading = true;
        const results = await this.$apiCall(`query SalesInvoicesEdit($id:ID!) {
                                                customer(id:$id) {
                                                  id,name,email,currency,term{id},salesRep{id}
                                                  billingAddress{line1,line2,city,province,postalCode}
                                                  shippingAddress{line1,line2,city,province,postalCode}
                                                }
                                              }`,
                                              {id:this.customer.id});
        this.loading = false;
        if (!results.error) {
          this.customer.loaded = true;
          Object.assign(this.customer, results.data.customer);
        }
      }
      this.invoice.email = this.customer.email;
      const billToAddress = this.customer.billingAddress;
      this.invoice.billToAddress = this.customer.name + '\n' + billToAddress.line1 + '\n' + (billToAddress.line2 ? billToAddress.line2 + '\n' : '') +
                              billToAddress.city + ' ' + billToAddress.province + '  ' + billToAddress.postalCode;
      const shipToAddress = this.customer.shippingAddress;
      this.invoice.shipToAddress = this.customer.name + '\n' + shipToAddress.line1 + '\n' + (shipToAddress.line2 ? shipToAddress.line2 + '\n' : '') +
                              shipToAddress.city + ' ' + shipToAddress.province + '  ' + shipToAddress.postalCode;
      for (const line of this.invoice.lines)
        this.setLineDetails(line, false);
      this.calculateTotals(true);
    },
    changeQuantityPrice(line, parentLine) {
      if (line.items)
        line.items.forEach(l => this.changeQuantityPrice(l, line));
      else
        line.amount = this.$roundNumber(line.quantity * line.unitPrice * (parentLine?.quantity || 1));
      this.calculateTotals(true);
    },
    changeAmount(line) {
      line.unitPrice = line.quantity ? this.$roundNumber(line.amount / line.quantity) : 0;
      this.calculateTotals(true);
    },
    cancel() {
      this.$router.back();
    },
    isLineIncomplete(line) {
      return !line.type || !line.id || !line.description ||
              line.quantity < 0 && line.type !== 'Item' ||
              line.unitPrice < 0 ||
              line.amount < 0 && line.type !== 'Item' ||
              (this.connectedToAccounting && !line.taxCodeId && !line.items?.length) ||
              line.items?.length && line.items.some(subLine => this.isLineIncomplete(subLine));
    },
    save() {
      if (!this.$refs.form.validate() || this.invoice.lines.some(line => this.isLineIncomplete(line))) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      if (!this.nonSOInventory || this.recurring)
        this.save2();
      else
        this.showConfirm = true;
    },
    async save2() {
      // Build request, especially information for all items being sold
      // eslint-disable-next-line no-unused-vars
      const {lines,voided,recurringTransaction,attachmentURL, ...input} = this.invoice;
      input.lines = this.invoice.lines.map(line => {
        let items;    // Only if this is a bundle with displayed items
        if (line.type === 'Bundle' && line.items) {
          items = line.items.map(bundleLine => (
             {type:bundleLine.type === 'Item' ? bundleLine.type : 'Product',
              inBondProduct:false,cases:false,id:bundleLine.id,sku:bundleLine.sku,description:bundleLine.description,
              quantity:bundleLine.quantity,amount:bundleLine.amount,taxCodeId:bundleLine.taxCodeId}
          ));
        }

        let amount = line.amount;
        if (input.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          amount = this.$roundNumber(line.amount / (1 + tax.salesRate/100));
        }

        // Make sure any salesOrderId actually exists for item
        const salesOrderId = this.salesOrdersForLine(line).find(so => so.salesOrderId === line.salesOrderId)?.salesOrderId ?? '';

        return {type:line.type,inBondProduct:line.inBondProduct,cases:line.cases,id:line.id,sku:line.sku,description:line.description,
                quantity:line.quantity,amount,taxCodeId:line.taxCodeId,
                salesOrderId:this.recurring ? '' : line.salesOrderId,
                items};
      });

      // If copying or existing invoice is made recurring, create a new recurring invoice (and leave original unchanged)
      if (this.copyInvoiceId || this.recurring && !this.invoice.recurringTransaction)
        delete input.id;

      if (this.recurring)
        input.recurringTransaction = this.recurringTransaction;
      input.fromRecurringTransactionId = this.fromRecurringTransactionId;

      this.saving = true;
      const results = await this.$apiCall(`mutation SalesInvoicesEdit($input: InvoiceInput!) {
                                             invoiceCrupdate(input:$input) {id}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.invoice.id?'Updated':'Created'}${this.invoice.draft?' draft':''} invoice to ${this.customer.name}`});
      }
    }
  }
}
</script>